import React, { createContext, useContext, useEffect, useState } from 'react';
import { supabase } from '../lib/supabase';
import { authService } from '../utils/authService';

const AuthContext = createContext({})

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return context
}

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null)
  const [userProfile, setUserProfile] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)

  useEffect(() => {
    // Get initial session - Use Promise chain
    supabase?.auth?.getSession()?.then(({ data: { session } }) => {
        if (session?.user) {
          setUser(session?.user)
          fetchUserProfile(session?.user?.id)
        }
        setLoading(false)
      })

    // Listen for auth changes - NEVER ASYNC callback
    const { data: { subscription } } = supabase?.auth?.onAuthStateChange(
      (event, session) => {
        if (session?.user) {
          setUser(session?.user)
          fetchUserProfile(session?.user?.id)  // Fire-and-forget, NO AWAIT
        } else {
          setUser(null)
          setUserProfile(null)
        }
        setLoading(false)
      }
    )

    return () => subscription?.unsubscribe()
  }, [])

  const fetchUserProfile = (userId) => {
    if (!userId) return;
    
    authService?.getUserProfile(userId)?.then(profile => {
        setUserProfile(profile);
        setError(null);
      })?.catch(err => {
        console.error('Error fetching user profile:', err);
        setError(err?.message || 'Failed to load user profile');
      });
  }

  const signUp = async (email, password, userData = {}) => {
    try {
      setError(null);
      const result = await authService?.signUp(email, password, userData);
      return result;
    } catch (err) {
      const errorMessage = err?.message || 'Failed to create account';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  }

  const signIn = async (email, password) => {
    try {
      setError(null);
      const result = await authService?.signIn(email, password);
      return result;
    } catch (err) {
      const errorMessage = err?.message || 'Failed to sign in';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  }

  const signOut = async () => {
    try {
      setError(null);
      await authService?.signOut();
      setUser(null);
      setUserProfile(null);
    } catch (err) {
      const errorMessage = err?.message || 'Failed to sign out';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  }

  const updateProfile = async (updates) => {
    try {
      setError(null);
      if (!user?.id) throw new Error('No authenticated user');
      
      const updatedProfile = await authService?.updateUserProfile(user?.id, updates);
      setUserProfile(updatedProfile);
      return updatedProfile;
    } catch (err) {
      const errorMessage = err?.message || 'Failed to update profile';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  }

  const updatePublisherProfile = async (profileData) => {
    try {
      setError(null);
      if (!user?.id) throw new Error('No authenticated user');
      
      const updatedProfile = await authService?.updatePublisherProfile(user?.id, profileData);
      
      // Refresh user profile to include updated publisher data
      fetchUserProfile(user?.id);
      return updatedProfile;
    } catch (err) {
      const errorMessage = err?.message || 'Failed to update publisher profile';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  }

  const updateBuyerProfile = async (profileData) => {
    try {
      setError(null);
      if (!user?.id) throw new Error('No authenticated user');
      
      const updatedProfile = await authService?.updateBuyerProfile(user?.id, profileData);
      
      // Refresh user profile to include updated buyer data
      fetchUserProfile(user?.id);
      return updatedProfile;
    } catch (err) {
      const errorMessage = err?.message || 'Failed to update buyer profile';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  }

  const resetPassword = async (email) => {
    try {
      setError(null);
      const result = await authService?.resetPassword(email);
      return result;
    } catch (err) {
      const errorMessage = err?.message || 'Failed to send reset email';
      setError(errorMessage);
      throw new Error(errorMessage);
    }
  }

  const value = {
    user,
    userProfile,
    loading,
    error,
    signUp,
    signIn,
    signOut,
    updateProfile,
    updatePublisherProfile,
    updateBuyerProfile,
    resetPassword,
    // Helper getters
    isAuthenticated: !!user,
    isPublisher: userProfile?.role === 'publisher',
    isBuyer: userProfile?.role === 'buyer',
    isAdmin: userProfile?.role === 'admin'
  }

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export default AuthProvider;